import * as React from 'react';

type LazyComponentClass = React.ComponentType<{}> | React.ReactElement;

type Props = {
  onLazyImport: () => Promise<any>,
  onLazyInit?: (a: any) => any
};

type State = {
  LoadedComponent: LazyComponentClass | undefined | null
};

class LazyComponent extends React.Component<Props, State> {
  state: State = {
    LoadedComponent: null
  };

  mounted = false;

  componentDidMount(): void {
    this.mounted = true;
    this.loadModule();
  }

  componentWillUnmount(): void {
    this.mounted = false;
  }

  loadModule(): void {
    const { onLazyImport } = this.props;

    onLazyImport()
      .then(module => this.handleModuleLoaded(module.default ? module.default : module))
      .catch(() => this.handleModuleLoaded(null));
  }

  handleModuleLoaded = (LoadedComponent?: LazyComponentClass | null): void => {
    const { onLazyInit } = this.props;

    if (!this.mounted) {
      // the module has been loaded too late - the component was already unmounted
      return;
    }

    if (LoadedComponent && (typeof LoadedComponent === 'function' || typeof LoadedComponent.type === 'function')) {
      this.setState({
        LoadedComponent: onLazyInit ? onLazyInit(LoadedComponent) : LoadedComponent
      });
    } else {
      this.setState({
        LoadedComponent: null // 404, not found
      });
    }
  };

  render(): React.ReactNode {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { onLazyImport, onLazyInit, ...componentProps } = this.props; // exclude onLazyImport and onLazyInit
    const { LoadedComponent } = this.state;

    // we could render a spinner if it's `null` but lazy module loading is happening quite fast so let's skip it
    // @ts-ignore - Component does not have any construct or call signatures
    return LoadedComponent ? <LoadedComponent {...componentProps} /> : null;
  }
}

export default LazyComponent;
